import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { Dropdown } from "primeng/dropdown";
import { SubSink } from "subsink";
import { AutomapperService } from "../../../core/automapper/automapper.service";
import { RiskService } from "../../../platform/modules/member/chase-detail/chase-detail-chart/risk/risk.service";
import { GridFilter } from "../../../shared/grid/models/grid-filter.model";
import { NationalProviderGridComponent } from "../../../shared/national-provider-grid/national-provider-grid.component";
import { NationalProviderGridService } from "../../../shared/national-provider-grid/national-provider-grid.service";
import { MasterProvider } from "../../../shared/national-provider-grid/national-provider.model";
import { DynamicControlDirective } from "../../dynamic-control-component.model";
import { DynamicFormEvent } from "../../dynamic-form-event.model";
import { FormService } from "../../form.service";
import { Textbox } from "../textbox/textbox.model";
import { ChaseDetailStateService } from "./../../../platform/modules/member/chase-detail/chase-detail-state.service";
import { ProviderDropdown } from "./provider-dropdown.model";
@Component({
  selector: "app-provider-dropdown",
  templateUrl: "./provider-dropdown.component.html",
  styleUrls: [
    "../textbox/textbox.component.scss",
    "../dropdown/dropdown.component.scss",
    "./provider-dropdown.component.scss",
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // providers: [RiskService], // NOTE: Keep commented out. Only here for testing.
})
export class ProviderDropdownComponent extends DynamicControlDirective<ProviderDropdown> implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(NationalProviderGridComponent, { static: false }) nationalProviderGridComponent: NationalProviderGridComponent;
  private sink = new SubSink();
  additionalFilters: GridFilter[] = [];

  @ViewChild("providerDropdown", { static: true }) primeProviderDropdown: Dropdown;
  visibleProviderSearchModal = false;
  providerSearchText: string;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly formService: FormService,
    private readonly nationalProviderGridService: NationalProviderGridService,
    private readonly riskService: RiskService,
    private readonly automapper: AutomapperService,
    private chaseDetailStateService: ChaseDetailStateService
  ) {
    super();
  }

  ngOnInit() {
    this.sink.add(
      this.control.statusChanges.subscribe(() => this.changeDetector.markForCheck()),
      this.formService.updateDom.subscribe(() => this.changeDetector.markForCheck())
    );

    this.createFilter();
  }

  private createFilter(): void {

    this.additionalFilters = [
      new GridFilter({
        input: new Textbox(),
        key: "globalSearchText",
        name: "Name",
        value: this.providerSearchText,
        validators: [Validators.minLength(3)],

      }),
    ];
  }

  ngAfterViewInit(): void {
    this.sink.add(
      this.primeProviderDropdown.onChange.subscribe((event: any) => this.onChange.next(new DynamicFormEvent({
        key: this.model.key,
        type: "notify",
        value: event.value,
        control: this.control,
        model: this.model,
      })))
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  get classes(): string {
    return this.getClasses("dropdown");
  }

  get options(): any[] {
    return this.model.options;
  }

  get enableSearch(): boolean {
    return !this.model.disabled && !this.model.isAdmin;
  }

  get visibleProviderSearchModalHack(): boolean {
    return this.visibleProviderSearchModal && this.enableSearch;
  }

  search(event: MouseEvent | null): void {
    if (event != null) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.additionalFilters[0].value = this.providerSearchText;
    this.nationalProviderGridComponent.refreshGrid();
  }

  openProviderSearchModal(): void {
    this.providerSearchText = "";
    this.nationalProviderGridService.data = [];
    this.visibleProviderSearchModal = true;
    this.changeDetector.markForCheck();
  }

  closeProviderSearchModal(): void {
    this.visibleProviderSearchModal = false;
    this.providerSearchText = "";
    this.nationalProviderGridService.data = [];
    this.changeDetector.markForCheck();
  }

  addAndSelect(provider: MasterProvider) {
    const providerNameUpper = provider.providerName.toUpperCase();
    const isUnknownProvider = providerNameUpper === "UNKNOWN PROVIDER";
    const hasProvider = isUnknownProvider
      ? this.options.some(a => a.extra.providerName === providerNameUpper)
      : this.options.some(a => a.extra.nationalProviderId === provider.nationalProviderId);
    if (hasProvider) {
      this.setValueByMasterProvider(provider, isUnknownProvider);
    } else {
      const chaseId = this.model.saveInfo.chaseId;
      this.nationalProviderGridService.addServiceProvider(chaseId, provider)
        .subscribe(providers => {
          this.riskService.data.value.providers = providers;
          this.model.options = providers.map(this.automapper.curry("Provider", "SelectableInput"));
          this.setValueByMasterProvider(provider, isUnknownProvider);
          this.chaseDetailStateService.setData({providers});
        });
    }
  }

  private setValueByMasterProvider({ nationalProviderId }: MasterProvider, isUnknownProvider?: boolean): void {
    const selectedOption = isUnknownProvider ?
      this.model.options.find(a => a.extra.providerName === "UNKNOWN PROVIDER")
      : this.model.options.find(a => a.extra.nationalProviderId === nationalProviderId);
    if (selectedOption != null) {
      const value = selectedOption.value;
      this.model.saveInfo.value = value;
      this.control.setValue(value);
      this.closeProviderSearchModal();
      this.primeProviderDropdown.focus();
    }
  }
}
